import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Intent, Card, Button, InputGroup, Callout, Text } from '@blueprintjs/core';
import moment from 'moment';

import ParameterList from '@f/components/shared/parameter-list/ParameterList';
import { findApiTokens, createApiToken, removeApiToken } from '@f/services/api-token/api-token.actions';

import cls from './ApiTokensPage.module.css';

const createTokenStates = {
  CREATE_TOKEN_HIDDEN: 'CREATE_TOKEN_HIDDEN',
  CREATE_TOKEN_INPUT: 'CREATE_TOKEN_INPUT',
  CREATE_TOKEN_RESULT: 'CREATE_TOKEN_RESULT',
};

class ApiTokenList extends PureComponent {
  static propTypes = {
    tokens: PropTypes.arrayOf(PropTypes.object),
    isProcessing: PropTypes.bool,
    errors: PropTypes.array,
    findApiTokens: PropTypes.func,
    createApiToken: PropTypes.func,
    removeApiToken: PropTypes.func,
  };

  state = {
    createTokenState: createTokenStates.CREATE_TOKEN_HIDDEN,
    tokenName: '',
    createdToken: null,
  };

  componentDidMount() {
    this.props.findApiTokens();
  }

  tokenNameChange = e => {
    this.setState({
      tokenName: e.target.value,
    });
  };

  hideCreateToken = () => {
    this.setState({
      createTokenState: createTokenStates.CREATE_TOKEN_HIDDEN,
    });
  };

  showCreateToken = () => {
    this.setState({
      createTokenState: createTokenStates.CREATE_TOKEN_INPUT,
    });
  };

  onCreate = async () => {
    const { tokenName } = this.state;

    const token = await this.props.createApiToken({ name: tokenName });

    this.setState({
      createTokenState: createTokenStates.CREATE_TOKEN_RESULT,
      createdToken: token,
    });
  };

  onRevoke = async id => {
    await this.props.removeApiToken(id);
  };

  render() {
    const { tokens } = this.props;
    const { createTokenState, createdToken } = this.state;

    return (
      <div className={cls.page}>
        <div className={cls.content}>
          {tokens.map(token => (
            <ApiToken {...token} onRevoke={this.onRevoke} key={token.id} />
          ))}

          <Button
            className={cx({
              [cls.hidden]: createTokenState !== createTokenStates.CREATE_TOKEN_HIDDEN,
            })}
            large
            intent={Intent.PRIMARY}
            text="Create new API Token"
            icon="add"
            onClick={this.showCreateToken}
          />

          <Card
            className={cx(cls.createContainer, {
              [cls.hidden]: createTokenState !== createTokenStates.CREATE_TOKEN_INPUT,
            })}
          >
            <InputGroup
              type="text"
              name="tokenName"
              intent={Intent.NONE}
              id="tokenName"
              placeholder="Enter token name..."
              required
              pattern="(?=.*\d)(?=.*[a-z]).{8,}"
              onChange={this.tokenNameChange}
            />
            <div className={cls.controls}>
              <Button
                className={cls.control}
                intent={Intent.PRIMARY}
                text="Create token"
                icon="add"
                onClick={this.onCreate}
              />
              <Button className={cls.control} minimal text="Cancel" onClick={this.hideCreateToken} />
            </div>
          </Card>

          {createTokenState === createTokenStates.CREATE_TOKEN_RESULT && (
            <Callout intent={Intent.SUCCESS}>
              <Text>
                {"Here's your new API Token "}
                <b>{createdToken?.name}</b>
              </Text>
              <Text>
                <code className={cls.token}>{createdToken?.token}</code>
              </Text>
              <Text>Make sure to copy it now and store securely. We won't be able to show it to you again.</Text>
            </Callout>
          )}
        </div>
      </div>
    );
  }
}

class ApiToken extends PureComponent {
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    createdAt: PropTypes.instanceOf(Date),
    onRevoke: PropTypes.func,
  };

  onRevoke = () => {
    const { id } = this.props;
    this.props.onRevoke(id);
  };

  render() {
    const { name, createdAt } = this.props;

    return (
      <Card className={cls.item}>
        <ParameterList
          itemClassName={cls.tokenAttribute}
          schema={[
            {
              label: 'Name',
              renderer: 'name',
            },
            {
              label: 'Created at',
              renderer: item =>
                moment(item.createdAt).calendar({
                  sameElse: 'lll',
                }),
            },
          ]}
          data={{ name, createdAt }}
        />
        <Button minimal intent={Intent.DANGER} text="Revoke" icon="trash" onClick={this.onRevoke} />
      </Card>
    );
  }
}

export default connect(
  state => {
    return {
      tokens: Object.values(state.apiToken.data),
      isProcessing: state.apiToken.find.processing > 0,
      errors: [state.apiToken.find.error],
    };
  },
  { findApiTokens, createApiToken, removeApiToken }
)(ApiTokenList);
